import React, { useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useHistory, useLocation } from "react-router-dom";

import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Collapse,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Chip,
} from "@material-ui/core";
import { useTheme, makeStyles } from "@material-ui/core/styles";

import { FilledTextField, FilledSelect } from "../../components/FormElements";
import { PROMO_CODE_TYPES, CURRENCIES } from "../../constants";
import { PromoContext, FleetContext } from "../../context";
import { promoSchemas } from "../../validation/schemas";
import { recParseObjectForForm } from "../../services/misc.service";

const useStyles = makeStyles((theme) => ({
  infoMessage: { opacity: 0.75 },
  marginRight: { marginRight: theme.spacing(1) },
  dateInput: { paddingRight: theme.spacing(1) },
  timeInput: { paddingLeft: theme.spacing(1) },
  dateTimeInput: {
    "& label": {
      width: "100%",
      "white-space": "nowrap",
      "text-overflow": "ellipsis",
      overflow: "hidden",
    },
  },
  fleetsFieldControl: {
    width: "100%",
    marginTop: theme.spacing(2),
  },
  fleetsField: {
    display: "flex",
    flexWrap: "wrap",
  },
  fleetsFieldItem: {
    background: theme.palette.background.dark,
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  tooltipIcon: { paddingLeft: theme.spacing(0.5) },
  tooltip: { fontSize: "0.75em" },
  fleetCell: { minWidth: "100px" },
  error: {
    padding: 0,
    background: "none",
  },
}));

const PromoWrite = () => {
  const MODES = ["new", "edit"];
  const initialForm = {
    code: "",
    active: true,
    percent_off: 0,
    currency: "USD",
    amount: 0,
    max_redemptions: 0,
    expired: "",
    description: "",
    fleets: [],
  };
  const theme = useTheme();
  const classes = useStyles(theme);
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { promoType: promoTypeParam, promoId } = useParams();
  const {
    codeAlreadyExists,
    getActivePromoCode,
    activePromoCode,
    updatePromoCode,
    createPromoCode,
  } = useContext(PromoContext);
  const { fleets } = useContext(FleetContext);
  const [open, setOpen] = useState(true);
  const [mode, setMode] = useState(MODES[0]);
  const [dirtyFields, setDirtyFields] = useState([]);
  const [invalids, setInvalids] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [codeNotUnique, setCodeNotUnique] = useState(false);
  const [promoTypeField, setPromoTypeField] = useState(PROMO_CODE_TYPES[0]);

  const handleClose = (e = null) => {
    if (e) e.stopPropagation();
    setOpen(false);
    history.goBack();
  };

  const handleSubmit = async () => {
    setLoading(true);
    const data = await promoSchemas[promoTypeField].cast(form, { stripUnknown: true });
    data.expired = data.expired.toISOString().replace(/T.*/, "T23:59:59.000Z");
    data.expired_by = data.expired;
    if (mode === "edit") {
      await updatePromoCode(promoId, promoTypeField, data);
    } else {
      await createPromoCode(promoTypeField, data);
    }
    setLoading(false);
    handleClose();
  };

  const findValidationError = (field) => {
    const alwaysShow = field === "code" && codeNotUnique;
    if (!alwaysShow && !dirtyFields.includes(field)) return null;
    return invalids.find((err) => err.includes(field));
  };

  useEffect(() => {
    if (promoId && promoTypeParam) {
      const loadPromo = async () => {
        await getActivePromoCode(promoId, promoTypeParam);
      };
      loadPromo();
    }
  }, [promoId, promoTypeParam]);

  useEffect(() => {
    if (activePromoCode) {
      const parsed = recParseObjectForForm(activePromoCode);
      const truncateExpired = parsed.expired ? parsed.expired.slice(0, 10) : "";
      setForm({ ...initialForm, ...parsed, expired: truncateExpired });
    } else setForm(initialForm);
  }, [activePromoCode]);

  useEffect(() => () => getActivePromoCode(null), []);

  useEffect(() => {
    const timeout = setTimeout(async () => {
      if (mode === "edit") return;
      try {
        await promoSchemas[promoTypeField].validateAt("code", form);
        setCodeNotUnique(false);
        const exists = await codeAlreadyExists(form.code);
        setCodeNotUnique(exists);
      } catch (err) {
        setCodeNotUnique(false);
      }
    }, 750);
    return () => clearTimeout(timeout);
  }, [form.code]);

  useEffect(() => {
    const validate = async () => {
      try {
        await promoSchemas[promoTypeField].validate(form, {
          abortEarly: false,
          recursive: true,
        });
        setInvalids([]);
      } catch (err) {
        setInvalids(err.errors);
      }
      if (codeNotUnique) setInvalids([...invalids, "Promo code already taken"]);
    };
    validate();
  }, [form, promoTypeField, codeNotUnique]);

  useEffect(() => {
    setMode(MODES.find((m) => location.pathname.includes(m)));
    const background = history?.location?.state?.background?.pathname;
    if (promoTypeParam) setPromoTypeField(promoTypeParam);
    else if (background) setPromoTypeField(background.substr(background.lastIndexOf("/") + 1));
  }, [location.pathname, promoTypeParam]);

  const standardTextField = (field, rest) => (
    <FilledTextField
      key={form?.currency}
      value={form[field]}
      label={t(`Promos.EditModal.${field}`)}
      onChange={(e) =>
        setForm({
          ...form,
          [field]: field === "code" ? e.target.value.toUpperCase().trim() : e.target.value,
        })
      }
      onBlur={() => setDirtyFields([...dirtyFields, field])}
      underlineColor={theme.palette.text.lightgrey}
      helperText={findValidationError(field)}
      error={!!findValidationError(field)}
      {...rest}
    />
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {t(`Promos.EditModal.${mode === "new" ? "CreatePromo" : "EditPromo"}`)}
      </DialogTitle>
      <DialogContent>
        <Grid>{standardTextField("code", { disabled: mode === "edit" })}</Grid>
        <Grid>
          <FilledSelect
            value={promoTypeField}
            label={t("Promos.EditModal.type")}
            onChange={(e) => setPromoTypeField(e.target.value)}
            FormControlProps={{ fullWidth: true, margin: "normal", disabled: mode === "edit" }}
          >
            {PROMO_CODE_TYPES.map((type) => (
              <MenuItem value={type} key={type}>
                {t(`Promos.Nav.${type}`)}
              </MenuItem>
            ))}
          </FilledSelect>
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={form?.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                value="active"
              />
            }
            label={t("Promos.EditModal.active")}
          />
        </Grid>
        <Collapse in={!!promoTypeField && promoTypeField !== "unlock"}>
          <Grid container justify="space-between">
            {promoTypeField === "bonus" ? (
              <>
                <Grid item style={{ paddingRight: theme.spacing(2) }} xs={12} md={5}>
                  <FilledSelect
                    value={form?.currency}
                    label={t("Promos.EditModal.currency")}
                    onChange={(e) => setForm({ ...form, currency: e.target.value })}
                    helperText={findValidationError("currency")}
                    FormControlProps={{
                      fullWidth: true,
                      margin: "normal",
                      disabled: mode === "edit",
                    }}
                    error={!!findValidationError("currency")}
                  >
                    {CURRENCIES.map((cur) => (
                      <MenuItem value={cur} key={cur}>
                        {cur}
                      </MenuItem>
                    ))}
                  </FilledSelect>
                </Grid>
                <Grid item xs={12} md={7}>
                  {standardTextField("amount", { mask: form.currency, disabled: mode === "edit" })}
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                {standardTextField("percent_off", { mask: "PERCENT_WHOLE" })}
              </Grid>
            )}
          </Grid>
        </Collapse>
        <Grid>{standardTextField("max_redemptions")}</Grid>
        <Grid>
          {standardTextField("expired", { type: "date", InputLabelProps: { shrink: true } })}
        </Grid>
        <Grid>{standardTextField("description", { multiline: true })}</Grid>
        <Grid item xs={12}>
          <FilledSelect
            label={t("Promos.EditModal.fleets")}
            value={form?.fleets || initialForm.fleets}
            onChange={(e) => {
              setForm({ ...form, fleets: e.target.value });
            }}
            FormControlProps={{
              className: classes.fleetsFieldControl,
            }}
            SelectProps={{
              multiple: true,
              renderValue: (selected) => (
                <div className={classes.fleetsField}>
                  {selected.map((uuid) => (
                    <Chip
                      key={uuid}
                      label={fleets.map((fleet) => {
                        if (fleet.uuid4 === uuid) {
                          return fleet.state ? `${fleet.name}, ${fleet.state}` : fleet.name;
                        }
                        return "";
                      })}
                      className={classes.fleetsFieldItem}
                    />
                  ))}
                </div>
              ),
            }}
          >
            {fleets.map((fleet) => (
              <MenuItem key={fleet.uuid4} value={fleet.uuid4}>
                {fleet.state ? `${fleet.name}, ${fleet.state}` : fleet.name}
              </MenuItem>
            ))}
          </FilledSelect>
        </Grid>
        {loading && <LinearProgress color="secondary" />}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t("Promos.EditModal.Cancel")}</Button>
        <Button onClick={handleSubmit} disabled={invalids?.length > 0}>
          {t("Promos.EditModal.Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromoWrite;
